import { gql } from '@apollo/client';

// Types for Invoice Generation

export interface GenerateFictitiousInvoicesInput {
  prefix: string;
  invoiceCount: number;
  startDate: Date;
  endDate: Date;
  linesPerInvoice: number;
  includeTip: boolean;
  numberingMode: 'automatic' | 'manual';
  manualStartNumber?: number;
  customTotalAmount?: number;
}

export interface InvoiceGenerationResult {
  success: boolean;
  generatedCount: number;
  totalAmount: number;
  firstInvoiceNumber: string;
  lastInvoiceNumber: string;
  message: string;
}

export interface AvailableProduct {
  id: string;
  nombre: string;
  precio: number;
  activo: boolean;
}

export interface ProductSummary {
  totalProducts: number;
  minPrice: number;
  maxPrice: number;
  avgPrice: number;
}

export interface LineOptionCalculation {
  linesPerInvoice: number;
  estimatedTotal: number;
  estimatedPerInvoice: number;
  productsUsed: number;
}

export interface LineOptionsResult {
  options: LineOptionCalculation[];
  productSummary: ProductSummary;
  customAmountSupported: boolean;
}

// ==================== QUERIES ====================

/**
 * Get available products for invoice generation
 */
export const GET_AVAILABLE_PRODUCTS = gql`
  query GetAvailableProducts {
    getAvailableProducts {
      id
      nombre
      precio
      activo
    }
  }
`;

/**
 * Get product summary (stats)
 */
export const GET_PRODUCT_SUMMARY = gql`
  query GetProductSummary {
    getProductSummary {
      totalProducts
      minPrice
      maxPrice
      avgPrice
    }
  }
`;

/**
 * Calculate line options based on invoice count
 */
export const CALCULATE_LINE_OPTIONS = gql`
  query CalculateLineOptions($invoiceCount: Int!) {
    calculateLineOptions(invoiceCount: $invoiceCount) {
      options {
        linesPerInvoice
        estimatedTotal
        estimatedPerInvoice
        productsUsed
      }
      productSummary {
        totalProducts
        minPrice
        maxPrice
        avgPrice
      }
      customAmountSupported
    }
  }
`;

// ==================== MUTATIONS ====================

/**
 * Generate fictitious invoices
 */
export const GENERATE_FICTITIOUS_INVOICES = gql`
  mutation GenerateFictitiousInvoices($input: GenerateFictitiousInvoicesInput!) {
    generateFictitiousInvoices(input: $input) {
      success
      generatedCount
      totalAmount
      firstInvoiceNumber
      lastInvoiceNumber
      message
    }
  }
`;

// ==================== SYNC ELECTRONIC INVOICES ====================

export interface MissingInvoiceInfo {
  id: number;
  numeroFactura: string;
  total: number;
  createdAt: string;
}

export interface MissingElectronicInvoicesResult {
  totalInvoices: number;
  totalElectronic: number;
  missingCount: number;
  missingInvoices: MissingInvoiceInfo[];
}

export interface SyncElectronicInvoicesResult {
  success: boolean;
  syncedCount: number;
  skippedCount: number;
  errors: string[];
  message: string;
}

/**
 * Check missing electronic invoices
 */
export const CHECK_MISSING_ELECTRONIC_INVOICES = gql`
  query CheckMissingElectronicInvoices($prefix: String, $startDate: DateTime, $endDate: DateTime) {
    checkMissingElectronicInvoices(prefix: $prefix, startDate: $startDate, endDate: $endDate) {
      totalInvoices
      totalElectronic
      missingCount
      missingInvoices {
        id
        numeroFactura
        total
        createdAt
      }
    }
  }
`;

/**
 * Sync missing electronic invoices
 */
export const SYNC_MISSING_ELECTRONIC_INVOICES = gql`
  mutation SyncMissingElectronicInvoices($prefix: String, $limit: Int, $startDate: DateTime, $endDate: DateTime) {
    syncMissingElectronicInvoices(prefix: $prefix, limit: $limit, startDate: $startDate, endDate: $endDate) {
      success
      syncedCount
      skippedCount
      errors
      message
    }
  }
`;
